import { Fragment, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { CircleCheck, CircleDot, Plus, X } from 'lucide-react';
import { ISSUE_STATES, bulkIssues, createIssue, labelIssue, listIssues, listLabels, listMilestones, listRepositories, updateIssue } from '@mkcore/dev/schema';
import {
  Badge,
  BulkBar,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Mono,
  NativeSelect,
  PageHeader,
  SelectAllCheckbox,
  SelectRowCheckbox,
  Skeleton,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
  toast,
  useSelection,
} from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { useListReturn } from '@/lib/list-return';
import { connectionsQuery } from '@/features/admin/connections-panel';
import { AssigneesInput } from '@/features/dev/assignees';
import { MarkdownEditor } from '@/components/markdown-editor';
import { SavedViewActions, SavedViewSelect, findSavedView, savedViewsQuery } from '@/components/saved-views';
import { IssueFilterBuilder, parseIssueFilter } from '@/features/dev/issue-filter';
import { projectsQuery } from '@/features/projects/queries';
import { ItemAgentBadge, useItemAgentStatus } from '@/features/agents/item-status';

const search = z.object({
  projectId: z.string().optional(),
  repositoryId: z.string().optional(),
  state: z.enum(ISSUE_STATES).optional(),
  label: z.string().optional(),
  milestoneId: z.string().optional(),
  assignee: z.string().optional(),
  q: z.string().optional(),
  /** Advanced filter, a JSON group of conditions joined with and/or (CO-0404). */
  filter: z.string().optional(),
  sort: z.enum(['updated', 'created', 'number']).optional(),
  /** Group the list by state, repository, milestone, assignee or a label group (label:severity), CO-0389. */
  group: z.string().optional(),
});
type Search = z.infer<typeof search>;
export type IssueListSearch = Search;

/** The list key of the saved views of this list (CO-0402); a view's params are the search of the page. */
const LIST_KEY = 'dev.issue.list';
/** Where the list was left (#1349): its filters, loaded pages and scroll position, for the way back from an issue ($id.tsx). */
const RETURN_KEY = 'dev.issues';

/** The filters of a saved view as a search of this page; what no longer fits (a removed option) is left out. */
const searchOf = (params: Record<string, unknown>): Search => {
  const shape: Record<string, z.ZodType | undefined> = search.shape;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) if (shape[k]?.safeParse(v).success) out[k] = v;
  return out as Search;
};

export const Route = createFileRoute('/_app/dev/issues/')({
  validateSearch: search,
  component: Issues,
  // The list uses the full width of the screen (#1503).
  staticData: { fullWidth: true },
});

const reposQuery = queryOptions({ queryKey: ['dev', 'repo', 'list', 'enabled'], queryFn: () => call(listRepositories, { enabled: true }) });
const issuesQuery = (s: Search) =>
  infiniteQueryOptions({
    queryKey: ['dev', 'issue', 'list', s],
    queryFn: ({ pageParam }) =>
      call(listIssues, {
        ...(s.projectId && { projectId: s.projectId }),
        ...(s.repositoryId && { repositoryId: s.repositoryId }),
        state: s.state ?? 'open',
        ...(s.label && { labels: [s.label] }),
        ...(s.milestoneId && { milestoneId: s.milestoneId }),
        ...(s.assignee && { assignee: s.assignee }),
        ...(s.q && { q: s.q }),
        ...(s.filter && { filter: s.filter }),
        sort: s.sort ?? 'updated',
        ...(pageParam && { cursor: pageParam }),
      }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** Mirrored issues over all repositories (CO-0391, CO-0392, CO-0396): filter on project, repository, state, label, milestone, assignee and words, and save the filters as a personal view (CO-0402). */
function Issues() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const repos = useQuery(reposQuery);
  const projects = useInfiniteQuery(projectsQuery({ limit: 200 }));
  const withRepos = new Set((repos.data?.items ?? []).map((r) => r.projectId).filter(Boolean));
  const projectList = (projects.data?.pages.flatMap((p) => p.items) ?? []).filter((p) => withRepos.has(p.id));
  const labels = useQuery({ queryKey: ['dev', 'label', 'list', s.repositoryId], queryFn: () => call(listLabels, s.repositoryId ? { repositoryId: s.repositoryId } : {}) });
  const milestones = useQuery({ queryKey: ['dev', 'milestone', 'list', s.repositoryId], queryFn: () => call(listMilestones, { repositoryId: s.repositoryId, state: 'all' }), enabled: !!s.repositoryId });
  const q = useInfiniteQuery(issuesQuery(s));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  useListReturn(RETURN_KEY, s, { pages: q.data?.pages.length ?? 0, ready: !!q.data, hasMore: q.hasNextPage, loading: q.isFetching, error: q.isError, loadMore: () => void q.fetchNextPage() });
  const agentStatus = useItemAgentStatus('issue', rows.map((i) => i.id));
  const labelNames = [...new Set((labels.data?.items ?? []).map((l) => l.name))].sort();
  const conn = useQuery(connectionsQuery);
  const linked = conn.data?.items.some((c) => c.provider === 'forgejo' && c.status === 'active') ?? false;
  const canUpdate = useCan('dev:update');
  const canCreate = useCan('dev:create');
  const toggle = useActionMutation(updateIssue, { invalidate: [['dev', 'issue']] });
  const relabel = useActionMutation(labelIssue, { invalidate: [['dev', 'issue']] });
  const editable = linked && canUpdate;
  // Inline editing (CO-0391): the labels of every mirrored organisation, filtered per row to its repository.
  const allLabels = useQuery({ queryKey: ['dev', 'label', 'list', 'all'], queryFn: () => call(listLabels, {}), enabled: editable });
  const labelsFor = (repositoryId: string, have: string[]) => {
    const orgId = repos.data?.items.find((r) => r.id === repositoryId)?.forgeOrgId;
    const names = (allLabels.data?.items ?? []).filter((l) => l.repositoryId === repositoryId || (l.repositoryId === null && l.forgeOrgId === orgId)).map((l) => l.name);
    return [...new Set(names)].filter((n) => !have.includes(n)).sort();
  };
  // Bulk (CO-0397): select rows, then one action for all of them.
  const selection = useSelection(rows.map((r) => r.id));
  const bulk = useActionMutation(bulkIssues, {
    invalidate: [['dev', 'issue']],
    onSuccess: (r) => {
      const failed = r.results.filter((x) => !x.ok);
      if (failed.length) toast.error(t('issues.bulkFailed', { ok: r.results.length - failed.length, failed: failed.length, detail: failed[0]!.detail ?? failed[0]!.code }));
      else toast.success(t('issues.bulkDone', { count: r.results.length }));
      selection.clear();
    },
  });
  const [bulkAssignees, setBulkAssignees] = useState('');
  const runBulk = (change: { state?: 'open' | 'closed'; addLabels?: string[]; removeLabels?: string[]; assignees?: string[] }) =>
    bulk.mutate({ input: { ids: [...selection.selected], ...change } });
  const selectedLabels = [...new Set(rows.filter((r) => selection.isSelected(r.id)).flatMap((r) => r.labels))].sort();
  // Grouping (CO-0389): the groups follow the sort of the list.
  const labelGroups = [...new Set(labelNames.map((n) => (n.includes('/') ? n.slice(0, n.lastIndexOf('/')) : null)).filter((g): g is string => !!g))].sort();
  const groupKey = (i: (typeof rows)[number]): string => {
    const g = s.group ?? '';
    if (g === 'state') return t(`issues.state.${i.state}`);
    if (g === 'repository') return i.repository;
    if (g === 'milestone') return i.milestone?.title ?? t('issues.noMilestone');
    if (g === 'assignee') return i.assignees[0] ?? t('issues.unassigned');
    if (g.startsWith('label:')) return i.labels.find((l) => l.startsWith(`${g.slice(6)}/`)) ?? t('issues.withoutGroup', { group: g.slice(6) });
    return '';
  };
  const groups = new Map<string, typeof rows>();
  for (const r of rows) groups.set(groupKey(r), [...(groups.get(groupKey(r)) ?? []), r]);
  const columns = 4 + (editable ? 2 : 0);
  // Saved views (CO-0402): the chosen view is the one whose filters are those in the URL.
  const savedViews = useQuery(savedViewsQuery(LIST_KEY));
  const currentView = findSavedView(savedViews.data?.items ?? [], s);
  const advanced = parseIssueFilter(s.filter);
  const [advancedOpen, setAdvancedOpen] = useState(!!advanced);
  const filtered = Object.values(s).some((v) => v !== undefined && v !== '');
  return (
    <>
      <PageHeader
        eyebrow={t('nav.dev')}
        title={t('issues.title')}
        description={t('issues.description')}
        actions={linked && canCreate && <NewIssue repositories={(repos.data?.items ?? []).map((r) => ({ id: r.id, fullName: r.fullName }))} defaultRepositoryId={s.repositoryId} />}
      />
      <div className="mb-3 flex flex-wrap items-center gap-2" role="toolbar" aria-label={t('issues.views')}>
        <SavedViewSelect
          listKey={LIST_KEY}
          value={currentView?.id ?? ''}
          custom={filtered}
          onChange={(v) => void navigate({ search: v ? searchOf(v.params) : {}, replace: true })}
        />
        <SavedViewActions listKey={LIST_KEY} value={currentView?.id ?? ''} params={s} />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('issues.search')} className="w-64">
          {(p) => <Input {...p} key={s.q ?? ''} type="search" defaultValue={s.q ?? ''} placeholder={t('issues.searchHint')} onKeyDown={(e) => e.key === 'Enter' && set({ q: (e.target as HTMLInputElement).value.trim() || undefined })} />}
        </Field>
        {projectList.length > 0 && (
          <Field label={t('issues.project')} className="w-48">
            {(p) => (
              <NativeSelect {...p} value={s.projectId ?? ''} onChange={(e) => set({ projectId: e.target.value || undefined })}>
                <option value="">{t('common.all')}</option>
                {projectList.map((pr) => (
                  <option key={pr.id} value={pr.id}>
                    {pr.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
        <Field label={t('issues.repository')} className="w-56">
          {(p) => (
            <NativeSelect {...p} value={s.repositoryId ?? ''} onChange={(e) => set({ repositoryId: e.target.value || undefined, milestoneId: undefined })}>
              <option value="">{t('common.all')}</option>
              {(repos.data?.items ?? []).map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fullName}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('common.status')} className="w-36">
          {(p) => (
            <NativeSelect {...p} value={s.state ?? 'open'} onChange={(e) => set({ state: e.target.value as Search['state'] })}>
              {ISSUE_STATES.map((st) => (
                <option key={st} value={st}>
                  {t(`issues.state.${st}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('issues.label')} className="w-48">
          {(p) => (
            <NativeSelect {...p} value={s.label ?? ''} onChange={(e) => set({ label: e.target.value || undefined })}>
              <option value="">{t('common.all')}</option>
              {labelNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {s.repositoryId && (
          <Field label={t('issues.milestone')} className="w-48">
            {(p) => (
              <NativeSelect {...p} value={s.milestoneId ?? ''} onChange={(e) => set({ milestoneId: e.target.value || undefined })}>
                <option value="">{t('common.all')}</option>
                {(milestones.data?.items ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title} ({Math.round(m.progress * 100)}%)
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
        <Field label={t('issues.assignee')} className="w-36">
          {(p) => <Input {...p} key={s.assignee ?? ''} defaultValue={s.assignee ?? ''} onBlur={(e) => set({ assignee: e.target.value.trim() || undefined })} />}
        </Field>
        <Button variant={advanced ? 'primary' : 'secondary'} aria-expanded={advancedOpen} onClick={() => setAdvancedOpen(!advancedOpen)}>
          {t('issues.advanced')}
        </Button>
        <Field label={t('issues.groupBy')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.group ?? ''} onChange={(e) => set({ group: e.target.value || undefined })}>
              <option value="">{t('issues.noGrouping')}</option>
              <option value="state">{t('common.status')}</option>
              <option value="repository">{t('issues.repository')}</option>
              <option value="milestone">{t('issues.milestone')}</option>
              <option value="assignee">{t('issues.assignee')}</option>
              {labelGroups.map((g) => (
                <option key={g} value={`label:${g}`}>
                  {t('issues.labelGroup', { group: g })}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('issues.sort')} className="w-40">
          {(p) => (
            <NativeSelect {...p} value={s.sort ?? 'updated'} onChange={(e) => set({ sort: e.target.value as Search['sort'] })}>
              <option value="updated">{t('issues.sortUpdated')}</option>
              <option value="created">{t('issues.sortCreated')}</option>
              <option value="number">{t('issues.sortNumber')}</option>
            </NativeSelect>
          )}
        </Field>
      </div>
      {advancedOpen && <IssueFilterBuilder initial={advanced} onCommit={(filter) => set({ filter })} />}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('issues.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              {editable && (
                <Th className="w-8">
                  <SelectAllCheckbox selection={selection} label={t('common.selectAll')} />
                </Th>
              )}
              <Th>{t('issues.issue')}</Th>
              <Th className="hidden lg:table-cell">{t('issues.labels')}</Th>
              <Th className="hidden md:table-cell">{t('issues.assignees')}</Th>
              <Th className="hidden md:table-cell">{t('common.updated')}</Th>
              {editable && <Th className="w-12 text-right">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {[...groups.entries()].map(([key, items]) => (
              <Fragment key={key || '-'}>
                {s.group && (
                  <Tr>
                    <Td colSpan={columns} className="bg-surface-alt text-sm font-bold">
                      {key} <span className="ml-1 font-normal text-fg-3 tabular">{items.length}</span>
                    </Td>
                  </Tr>
                )}
                {items.map((i) => (
                  <Tr key={i.id} data-state={selection.isSelected(i.id) ? 'selected' : undefined}>
                    {editable && (
                      <Td>
                        <SelectRowCheckbox selection={selection} rowKey={i.id} label={t('issues.select', { number: i.number })} />
                      </Td>
                    )}
                    <Td>
                      <div className="flex items-baseline gap-2">
                        <Badge tone={i.state === 'open' ? 'success' : 'neutral'}>{t(`issues.state.${i.state}`)}</Badge>
                        <Link to="/dev/issues/$id" params={{ id: i.id }} className="font-bold hover:underline">
                          {i.title}
                        </Link>
                      </div>
                      <div className="mt-0.5 text-xs text-fg-3">
                        <Mono className="text-[11px]">
                          {i.repository}#{i.number}
                        </Mono>
                        {i.milestone && ` · ${i.milestone.title}`}
                        {i.commentCount > 0 && ` · ${t('issues.comments', { count: i.commentCount })}`}
                      </div>
                      <ItemAgentBadge status={agentStatus.get(i.id)} />
                    </Td>
                    <Td className="hidden lg:table-cell">
                      <div className="flex flex-wrap items-center gap-1">
                        {i.labels.map((l) => (
                          <Badge key={l} tone="outline" className="gap-1">
                            {l}
                            {editable && (
                              <button
                                type="button"
                                aria-label={t('issues.removeLabelOf', { label: l, number: i.number })}
                                className="hover:text-danger"
                                disabled={relabel.isPending}
                                onClick={() => relabel.mutate({ input: { id: i.id, remove: [l] } })}
                              >
                                <X className="size-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                        {editable && (
                          <NativeSelect
                            aria-label={t('issues.addLabelTo', { number: i.number })}
                            className="h-7 w-8 px-1 text-xs"
                            value=""
                            disabled={relabel.isPending}
                            onChange={(e) => e.target.value && relabel.mutate({ input: { id: i.id, add: [e.target.value] } })}
                          >
                            <option value="">+</option>
                            {labelsFor(i.repositoryId, i.labels).map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </NativeSelect>
                        )}
                      </div>
                    </Td>
                    <Td className="hidden md:table-cell text-sm text-fg-2">
                      {editable ? (
                        <AssigneesInput
                          key={i.assignees.join(',')}
                          label={t('issues.assigneesOf', { number: i.number })}
                          className="h-8 w-40"
                          value={i.assignees}
                          busy={toggle.isPending && toggle.variables?.input.id === i.id}
                          onSave={(assignees) => toggle.mutate({ input: { id: i.id, assignees } })}
                        />
                      ) : (
                        i.assignees.join(', ') || '—'
                      )}
                    </Td>
                    <Td className="hidden md:table-cell whitespace-nowrap tabular text-fg-2">{formatDateTime(i.updatedAt)}</Td>
                    {editable && (
                      <Td className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={i.state === 'open' ? t('issues.closeIssue', { number: i.number }) : t('issues.reopenIssue', { number: i.number })}
                          title={i.state === 'open' ? t('issues.close') : t('issues.reopen')}
                          loading={toggle.isPending && toggle.variables?.input.id === i.id}
                          onClick={() => toggle.mutate({ input: { id: i.id, state: i.state === 'open' ? 'closed' : 'open' } })}
                        >
                          {i.state === 'open' ? <CircleCheck /> : <CircleDot />}
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Fragment>
            ))}
          </TBody>
        </Table>
      )}
      {editable && (
        <BulkBar count={selection.count} label={t('issues.selected', { count: selection.count })} clearLabel={t('common.clearSelection')} onClear={selection.clear}>
          <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => runBulk({ state: 'closed' })}>
            {t('issues.close')}
          </Button>
          <Button size="sm" variant="secondary" disabled={bulk.isPending} onClick={() => runBulk({ state: 'open' })}>
            {t('issues.reopen')}
          </Button>
          <NativeSelect aria-label={t('issues.bulkAddLabel')} className="h-8 w-40 text-fg-1" value="" disabled={bulk.isPending} onChange={(e) => e.target.value && runBulk({ addLabels: [e.target.value] })}>
            <option value="">{t('issues.bulkAddLabel')}</option>
            {labelNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect aria-label={t('issues.bulkRemoveLabel')} className="h-8 w-40 text-fg-1" value="" disabled={bulk.isPending || !selectedLabels.length} onChange={(e) => e.target.value && runBulk({ removeLabels: [e.target.value] })}>
            <option value="">{t('issues.bulkRemoveLabel')}</option>
            {selectedLabels.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </NativeSelect>
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              runBulk({ assignees: [...new Set(bulkAssignees.split(',').map((x) => x.trim()).filter(Boolean))] });
              setBulkAssignees('');
            }}
          >
            <Input aria-label={t('issues.bulkAssign')} placeholder={t('issues.bulkAssign')} className="h-8 w-40 text-fg-1" value={bulkAssignees} onChange={(e) => setBulkAssignees(e.target.value)} />
            <Button size="sm" variant="secondary" type="submit" disabled={bulk.isPending}>
              {t('issues.assign')}
            </Button>
          </form>
        </BulkBar>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </>
  );
}

/** A new issue in one of the mirrored repositories (CO-0390), written to Forgejo with your linked account. */
function NewIssue({ repositories, defaultRepositoryId }: { repositories: { id: string; fullName: string }[]; defaultRepositoryId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [repositoryId, setRepositoryId] = useState(defaultRepositoryId ?? '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [label, setLabel] = useState('');
  const labels = useQuery({ queryKey: ['dev', 'label', 'list', repositoryId], queryFn: () => call(listLabels, { repositoryId }), enabled: open && !!repositoryId });
  const create = useActionMutation(createIssue, {
    invalidate: [['dev', 'issue']],
    success: (i) => t('issues.created', { number: i.number }),
    onSuccess: (i) => {
      setOpen(false);
      setTitle('');
      setBody('');
      setLabel('');
      void navigate({ to: '/dev/issues/$id', params: { id: i.id } });
    },
  });
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <Plus /> {t('issues.new')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('issues.new')}</DialogTitle>
          </DialogHeader>
          <form
            id="new-issue"
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate({ input: { repositoryId, title, body, labels: label ? [label] : [] } });
            }}
          >
            <Field label={t('issues.repository')}>
              {(p) => (
                <NativeSelect {...p} value={repositoryId} onChange={(e) => setRepositoryId(e.target.value)} required>
                  <option value="">{t('issues.chooseRepository')}</option>
                  {repositories.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.fullName}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('issues.titleField')}>{(p) => <Input {...p} value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={255} />}</Field>
            <Field label={t('issues.bodyField')}>{(p) => <MarkdownEditor {...p} value={body} onChange={setBody} rows={6} />}</Field>
            <Field label={t('issues.label')}>
              {(p) => (
                <NativeSelect {...p} value={label} onChange={(e) => setLabel(e.target.value)} disabled={!repositoryId}>
                  <option value="">{t('issues.noLabel')}</option>
                  {[...new Set((labels.data?.items ?? []).map((l) => l.name))].sort().map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          </form>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="new-issue" loading={create.isPending} disabled={!repositoryId || !title.trim()}>
              {t('issues.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
